import type { JobStatus } from '@client/components/_business/jobs/JobTable';
import type { RouterOutputs } from '@client/trpc/client';
import {
    FLEET_VEHICLE_VERIFICATION_STATUS_LABELS,
    type FleetVehicleVerificationStatus,
} from '@shared/constants/business/autograb';
import { CLAIM_CLOSURE_OUTCOME_LABELS } from '@shared/constants/business/claim-product-binding';
import { COMPLAINT_STATUS, type ComplaintStatus } from '@shared/constants/business/complaint';
import type { ControlTestOutcome } from '@shared/constants/business/control';
import type { DashboardStatus } from '@shared/constants/business/dashboard';
import type { EmailStatusLabel } from '@shared/constants/business/email-status';
import type { ExcessStatus } from '@shared/constants/business/excess';
import { INCIDENT_STATUSES, type IncidentStatus } from '@shared/constants/business/incident';
import {
    MSPR_GOVERNANCE_ASSESSMENT_RESULT_LABELS,
    type MsprGovernanceAssessmentResult,
} from '@shared/constants/business/mspr';
import type { OBLIGATION_STATUSES_KEYS } from '@shared/constants/business/obligation';
import { VEHICLE_INSPECTION_TRIAGE_LABELS } from '@shared/constants/business/questionnaire';
import { STAFF_TRAINING_STATUSES, type StaffTrainingStatus } from '@shared/constants/business/staff-training';
import { SUPPORT_SESSION_STATUS_LABELS, type SupportSessionStatus } from '@shared/constants/business/support-session';
import { TASK_STATUSES, TASK_STATUSES_LABELS } from '@shared/constants/business/task';
import { VEHICLE_ASSESSMENT_STATUS_LABELS } from '@shared/constants/business/vehicle-assessment';
import type { ScrapedDataStatus } from '@shared/prisma/enums';
import {
    type AiEvalCaseStatus,
    type AiEvalResultStatus,
    type AiEvalRunStatus,
    AiLodgementRunStatus,
    type AiPromptStatus,
    type ClaimClosureOutcome,
    type ClaimCompletionRequestStatus,
    type ClaimProductBinding,
    type ClaimStatusType,
    CommTemplateStatus,
    ComplianceAuditStatus,
    ComplianceAuditStepStatus,
    ComplianceAuditTemplateVersionStatus,
    type ContactVulnerabilityFlagStatus,
    type FaqStatus,
    type InboundEmailArchiveStatus,
    type IntegrationTestResultStatus,
    LodgementLinkStatus,
    LodgementSubmissionStatus,
    type MarketPdsStatus,
    type MessageVulnerabilityStatus,
    type OrgDigestReportStatus,
    ProductVersionStatus,
    type ScheduledUserAttestationStatus,
    type UserInvitationStatus,
    type VehicleAssessmentStatus,
    VehicleInspectionStatus,
    type WhatsNewReleaseStatus,
} from '@shared/prisma/enums';
import { INTEGRATION_STEP_LABELS } from '@shared/schemas/dynamic-forms/integrations/descriptor';
import type { AgentAssessmentResult } from '@shared/types/agent-assessment';
import {
    type CheckStatus,
    type ComplianceOverallResult,
    OVERALL_RESULT_LABELS,
} from '@shared/types/compliance/compliance-result';
import { COMPLIANCE_SCAN_STATUS_LABELS, type ComplianceScanStatus } from '@shared/types/compliance/compliance-scan';
import {
    AlertTriangle,
    Archive,
    Ban,
    CalendarCheck,
    CalendarClock,
    CalendarX,
    CheckCircle2,
    CircleCheck,
    CircleX,
    Clock,
    Copy,
    HelpCircle,
    Loader,
    PackageSearch,
    Pause,
    Pencil,
    RefreshCw,
    Send,
    ShieldAlert,
    ShieldCheck,
    ShieldQuestion,
    TimerOff,
    TrendingUp,
    Undo2,
    Upload,
    XCircle,
} from 'lucide-react';
import { type StatusRole, statusEntry } from './status-roles';

// Obligation status type
export type ObligationStatus = keyof typeof OBLIGATION_STATUSES_KEYS;

// FSG Finding status type (workflow state for compliance review)
export const FSG_FINDING_STATUSES = {
    COMPLIANT: 'COMPLIANT',
    NON_COMPLIANT: 'NON_COMPLIANT',
    UNCLEAR: 'UNCLEAR',
    NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const;

export type FsgFindingStatus = (typeof FSG_FINDING_STATUSES)[keyof typeof FSG_FINDING_STATUSES];

/**
 * One status as `StatusBadge` renders it. Build entries with `statusEntry(role, label, icon?)`: the
 * role owns every colour field, so a map names what a status means and never a colour. See
 * `status-roles.ts` for the role table (CUR-1337).
 */
export interface StatusConfig {
    /** What the status means in its workflow; the single source of the colour fields below */
    role: StatusRole;
    /** Ant Design tag hue for the outlined variant */
    color: string;
    /** CSS colour value for the dot variant and other custom styling */
    cssColor: string;
    /** Human-readable label */
    label: string;
    /** Tailwind background class for a tinted chip (a semantic `-subtle` surface or `bg-muted`) */
    bgClass: string;
    /** Tailwind text colour class for the filled pill and tinted chips — the `-foreground` partner */
    textClass: string;
    /** Tailwind border colour class for a tinted chip — the `-border` partner of `bgClass` */
    borderClass?: string;
    /** Role-coloured border of the filled pill, which has no fill of its own */
    edgeClass?: string;
    /** Optional icon component (from lucide-react) */
    icon?: React.ComponentType<{ className?: string }>;
}

export const INCIDENT_STATUS_CONFIG: Record<IncidentStatus, StatusConfig> = {
    [INCIDENT_STATUSES.open]: statusEntry('open', 'Open'),
    [INCIDENT_STATUSES.closed]: statusEntry('closed', 'Closed'),
};

export const SERVICE_API_TOKEN_STATUS_CONFIG = {
    active: statusEntry('open', 'Active'),
    revoked: statusEntry('failed', 'Revoked'),
} as const satisfies Record<string, StatusConfig>;

export const COMPLAINT_STATUS_CONFIG: Record<ComplaintStatus, StatusConfig> = {
    [COMPLAINT_STATUS.OPEN]: statusEntry('open', 'Open'),
    [COMPLAINT_STATUS.CLOSED]: statusEntry('closed', 'Closed'),
    [COMPLAINT_STATUS.RE_OPENED]: statusEntry('attention', 'Re-opened'),
};

export const OBLIGATION_STATUS_CONFIG: Record<ObligationStatus, StatusConfig> = {
    PUBLISHED: statusEntry('open', 'Published'),
    DRAFT: statusEntry('idle', 'Draft'),
    AWAITING_REVIEW: statusEntry('attention', 'Awaiting review'),
    NEW_VERSION_AWAITING_REVIEW: statusEntry('attention', 'New version — awaiting review'),
    DECOMMISSIONED: statusEntry('closed', 'Decommissioned'),
};

export const PRODUCT_VERSION_STATUS_CONFIG: Record<ProductVersionStatus, StatusConfig> = {
    [ProductVersionStatus.PUBLISHED]: statusEntry('open', 'Published', CircleCheck),
    [ProductVersionStatus.DRAFT]: statusEntry('idle', 'Draft', Pencil),
};

// Lodgement v2 (CUR-1407): a public link is active, paused (drafts stay resumable) or revoked
// (terminal; kept listed so a claimant's "this link isn't valid" can be traced).
export const LODGEMENT_LINK_STATUS_CONFIG: Record<LodgementLinkStatus, StatusConfig> = {
    [LodgementLinkStatus.ACTIVE]: statusEntry('open', 'Active', CircleCheck),
    [LodgementLinkStatus.PAUSED]: statusEntry('attention', 'Paused', Pause),
    [LodgementLinkStatus.REVOKED]: statusEntry('failed', 'Revoked', Ban),
};

// Lodgement v2 (CUR-1408): the submission's own vocabulary. Submitted and Processing are both
// in flight; Failed is the "Needs attention" queue; Rejected, Expired and Withdrawn ended without a claim.
// `WITHDRAWN` is not stored (CUR-2230): the server derives it for a completion draft whose request was
// withdrawn before it ran out, which the table keeps as EXPIRED.
export const LODGEMENT_SUBMISSION_STATUS_CONFIG: Record<LodgementSubmissionStatus | 'WITHDRAWN', StatusConfig> = {
    [LodgementSubmissionStatus.DRAFT]: statusEntry('idle', 'Draft', Pencil),
    [LodgementSubmissionStatus.SUBMITTED]: statusEntry('pending', 'Submitted', Send),
    [LodgementSubmissionStatus.PROCESSING]: statusEntry('pending', 'Processing', Loader),
    [LodgementSubmissionStatus.PROCESSED]: statusEntry('succeeded', 'Processed', CircleCheck),
    [LodgementSubmissionStatus.FAILED]: statusEntry('failed', 'Failed', XCircle),
    [LodgementSubmissionStatus.REJECTED]: statusEntry('cancelled', 'Rejected', Ban),
    [LodgementSubmissionStatus.EXPIRED]: statusEntry('cancelled', 'Expired', TimerOff),
    WITHDRAWN: statusEntry('cancelled', 'Withdrawn', Undo2),
};

// Deferred product binding (CUR-2163): a claim recorded before its product is known waits on a
// person to choose it, so it needs attention; once chosen the binding is not a workflow state.
export const CLAIM_PRODUCT_BINDING_STATUS_CONFIG = {
    PENDING: statusEntry('attention', 'Awaiting product', PackageSearch),
} as const satisfies Partial<Record<ClaimProductBinding, StatusConfig>>;

// Where a request for the client to complete a claim stands (CUR-2195). Sent waits on the client, work in
// flight; completed ended well; withdrawn was let go.
export const CLAIM_COMPLETION_REQUEST_STATUS_CONFIG = {
    SENT: statusEntry('pending', 'Asked to complete', Send),
    COMPLETED: statusEntry('succeeded', 'Completed', CircleCheck),
    WITHDRAWN: statusEntry('cancelled', 'Withdrawn'),
} as const satisfies Record<ClaimCompletionRequestStatus, StatusConfig>;

// Why a claim closed (CUR-2167). Every outcome ends the lifecycle, so all are neutral: the outcomes of
// a claim end it normally, and the exits of something that was never a claim end it without one.
export const CLAIM_CLOSURE_OUTCOME_STATUS_CONFIG = {
    CLAIM_SETTLED: statusEntry('closed', CLAIM_CLOSURE_OUTCOME_LABELS.CLAIM_SETTLED, CircleCheck),
    CLAIM_DECLINED: statusEntry('closed', CLAIM_CLOSURE_OUTCOME_LABELS.CLAIM_DECLINED, CircleX),
    WITHDRAWN: statusEntry('closed', CLAIM_CLOSURE_OUTCOME_LABELS.WITHDRAWN, Undo2),
    NOT_A_CLAIM: statusEntry('cancelled', CLAIM_CLOSURE_OUTCOME_LABELS.NOT_A_CLAIM, Ban),
    DUPLICATE: statusEntry('cancelled', CLAIM_CLOSURE_OUTCOME_LABELS.DUPLICATE, Copy),
} as const satisfies Record<ClaimClosureOutcome, StatusConfig>;

// How a product version's dates fit a claim's date of loss, beside each product in the "Choose the
// product" picker (CUR-2163): in force is healthy; a version outside the date needs a second look.
// The claim's current product among the ones it could move to (CUR-2193) is a plain tag: it classifies.
export const PRODUCT_VERSION_LOSS_DATE_FIT_STATUS_CONFIG = {
    covers: statusEntry('open', 'Covers the date of loss', CalendarCheck),
    'before-start': statusEntry('attention', 'Starts after the date of loss', CalendarClock),
    'after-end': statusEntry('attention', 'Ended before the date of loss', CalendarX),
} as const satisfies Record<string, StatusConfig>;

// AI claim lodgement from documents (CUR-1744): a run in flight is pending, a run waiting for the
// handler (choose the product, review the form) needs attention, and ended runs are neutral.
export const AI_LODGEMENT_RUN_STATUS_CONFIG: Record<AiLodgementRunStatus, StatusConfig> = {
    [AiLodgementRunStatus.UPLOADING]: statusEntry('idle', 'Adding files', Upload),
    [AiLodgementRunStatus.QUEUED]: statusEntry('pending', 'Queued', Clock),
    [AiLodgementRunStatus.ANALYSING]: statusEntry('pending', 'Analysing', Loader),
    [AiLodgementRunStatus.AWAITING_PRODUCT]: statusEntry('attention', 'Choose product', AlertTriangle),
    [AiLodgementRunStatus.PREFILLING]: statusEntry('pending', 'Filling in the claim form', Loader),
    [AiLodgementRunStatus.READY]: statusEntry('attention', 'Claim form ready', Pencil),
    [AiLodgementRunStatus.LODGED]: statusEntry('succeeded', 'Lodged', CircleCheck),
    [AiLodgementRunStatus.FAILED]: statusEntry('failed', 'Failed', XCircle),
    [AiLodgementRunStatus.CANCELLED]: statusEntry('cancelled', 'Cancelled', Ban),
    [AiLodgementRunStatus.EXPIRED]: statusEntry('cancelled', 'Expired', TimerOff),
};

/**
 * An integration element's step on a submitted form (docs/plan/form-integration-elements/plan.md).
 * The word comes from `integrationStepStatus`: `complete` for an answered value, `failed` and
 * `skipped` for the terminal non-successes every descriptor shares, and the descriptor's own
 * in-flight words otherwise. The in-flight words known across descriptors are labelled here;
 * any other renders neutral with its own word.
 */
export const INTEGRATION_STEP_STATUS_CONFIG = {
    complete: statusEntry('succeeded', INTEGRATION_STEP_LABELS.complete!, CircleCheck),
    failed: statusEntry('failed', INTEGRATION_STEP_LABELS.failed!, XCircle),
    skipped: statusEntry('cancelled', INTEGRATION_STEP_LABELS.skipped!, Ban),
    idle: statusEntry('idle', INTEGRATION_STEP_LABELS.idle!, Pause),
    challenged: statusEntry('idle', INTEGRATION_STEP_LABELS.challenged!, Clock),
    requested: statusEntry('idle', INTEGRATION_STEP_LABELS.requested!, Send),
    found: statusEntry('idle', INTEGRATION_STEP_LABELS.found!, Clock),
    unverified: statusEntry('attention', INTEGRATION_STEP_LABELS.unverified!, AlertTriangle),
} satisfies Record<string, StatusConfig>;

// CUR-424 — file-audit template versions share the DRAFT/PUBLISHED shape of product
// versions but carry their own enum, so renames in either stay independent.
export const COMPLIANCE_AUDIT_TEMPLATE_VERSION_STATUS_CONFIG: Record<
    ComplianceAuditTemplateVersionStatus,
    StatusConfig
> = {
    [ComplianceAuditTemplateVersionStatus.PUBLISHED]: statusEntry('open', 'Published', CircleCheck),
    [ComplianceAuditTemplateVersionStatus.DRAFT]: statusEntry('idle', 'Draft', Pencil),
};

export const FSG_FINDING_STATUS_CONFIG: Record<FsgFindingStatus, StatusConfig> = {
    [FSG_FINDING_STATUSES.COMPLIANT]: statusEntry('succeeded', 'Compliant'),
    [FSG_FINDING_STATUSES.NON_COMPLIANT]: statusEntry('failed', 'Non-Compliant'),
    [FSG_FINDING_STATUSES.UNCLEAR]: statusEntry('attention', 'Unclear'),
    [FSG_FINDING_STATUSES.NOT_APPLICABLE]: statusEntry('cancelled', 'N/A'),
};

// Task status type (dynamically calculated based on dueDate and isCompleted)
export type TaskStatus = keyof typeof TASK_STATUSES;

export const TASK_STATUS_CONFIG: Record<TaskStatus, StatusConfig> = {
    [TASK_STATUSES.NOT_APPLICABLE]: statusEntry('idle', TASK_STATUSES_LABELS.NOT_APPLICABLE),
    [TASK_STATUSES.OVERDUE]: statusEntry('breached', TASK_STATUSES_LABELS.OVERDUE),
    [TASK_STATUSES.DUE_SOON]: statusEntry('attention', TASK_STATUSES_LABELS.DUE_SOON),
    [TASK_STATUSES.NOT_DUE]: statusEntry('open', TASK_STATUSES_LABELS.NOT_DUE),
    [TASK_STATUSES.DONE]: statusEntry('closed', TASK_STATUSES_LABELS.DONE),
    [TASK_STATUSES.CANCELLED]: statusEntry('cancelled', TASK_STATUSES_LABELS.CANCELLED),
};

// Document Analysis status type
export const DOCUMENT_ANALYSIS_STATUSES = {
    PREVIEW: 'PREVIEW',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    ANALYZING: 'ANALYZING',
    EXTRACTING_TEXT: 'EXTRACTING_TEXT',
    SEGMENTING: 'SEGMENTING',
    PENDING: 'PENDING',
    CANCELLED: 'CANCELLED',
} as const;

export type DocumentAnalysisStatus = (typeof DOCUMENT_ANALYSIS_STATUSES)[keyof typeof DOCUMENT_ANALYSIS_STATUSES];

export const DOCUMENT_ANALYSIS_STATUS_CONFIG: Record<DocumentAnalysisStatus, StatusConfig> = {
    [DOCUMENT_ANALYSIS_STATUSES.PREVIEW]: statusEntry('failed', 'Failed'),
    [DOCUMENT_ANALYSIS_STATUSES.COMPLETED]: statusEntry('succeeded', 'Completed'),
    [DOCUMENT_ANALYSIS_STATUSES.FAILED]: statusEntry('failed', 'Failed'),
    [DOCUMENT_ANALYSIS_STATUSES.ANALYZING]: statusEntry('pending', 'Analysing'),
    [DOCUMENT_ANALYSIS_STATUSES.EXTRACTING_TEXT]: statusEntry('pending', 'Extracting'),
    [DOCUMENT_ANALYSIS_STATUSES.SEGMENTING]: statusEntry('pending', 'Segmenting'),
    [DOCUMENT_ANALYSIS_STATUSES.PENDING]: statusEntry('idle', 'Pending'),
    [DOCUMENT_ANALYSIS_STATUSES.CANCELLED]: statusEntry('cancelled', 'Cancelled'),
};

// Email status type
export const EMAIL_STATUSES = {
    DELIVERED: 'Delivered',
    PROCESSING: 'Processing',
    DEFERRED: 'Deferred',
    FAILED: 'Failed',
    BLOCKED: 'Blocked',
    UNSUBSCRIBED: 'Unsubscribed',
    NO_EMAILS: 'NO_EMAILS',
    // `satisfies` links these labels to the shared vocabulary the server derives — renaming a label
    // in `@shared/constants/business/email-status` fails to compile here. (`NO_EMAILS` is a
    // client-only synthetic for the null/no-events case; the DB never stores it.)
} as const satisfies Record<string, EmailStatusLabel | 'NO_EMAILS'>;

export type EmailStatusType = (typeof EMAIL_STATUSES)[keyof typeof EMAIL_STATUSES];

export const EMAIL_STATUS_CONFIG: Record<EmailStatusType, StatusConfig> = {
    [EMAIL_STATUSES.DELIVERED]: statusEntry('succeeded', 'Delivered'),
    [EMAIL_STATUSES.PROCESSING]: statusEntry('pending', 'Processing'),
    [EMAIL_STATUSES.DEFERRED]: statusEntry('attention', 'Deferred'),
    [EMAIL_STATUSES.FAILED]: statusEntry('failed', 'Failed'),
    [EMAIL_STATUSES.BLOCKED]: statusEntry('attention', 'Blocked'),
    [EMAIL_STATUSES.UNSUBSCRIBED]: statusEntry('cancelled', 'Unsubscribed'),
    [EMAIL_STATUSES.NO_EMAILS]: statusEntry('idle', 'No emails'),
};

// Message Classification status type (workflow state for auto-detect inbox)
export const MESSAGE_CLASSIFICATION_STATUSES = {
    PENDING: 'PENDING',
    PROCESSED: 'PROCESSED',
    ERROR: 'ERROR',
    REVIEWED: 'REVIEWED',
    SKIPPED: 'SKIPPED',
} as const;

export type MessageClassificationStatus =
    (typeof MESSAGE_CLASSIFICATION_STATUSES)[keyof typeof MESSAGE_CLASSIFICATION_STATUSES];

export const MESSAGE_CLASSIFICATION_STATUS_CONFIG: Record<MessageClassificationStatus, StatusConfig> = {
    [MESSAGE_CLASSIFICATION_STATUSES.PENDING]: statusEntry('idle', 'Pending'),
    [MESSAGE_CLASSIFICATION_STATUSES.PROCESSED]: statusEntry('succeeded', 'Processed'),
    [MESSAGE_CLASSIFICATION_STATUSES.ERROR]: statusEntry('failed', 'Error'),
    [MESSAGE_CLASSIFICATION_STATUSES.REVIEWED]: statusEntry('closed', 'Reviewed'),
    [MESSAGE_CLASSIFICATION_STATUSES.SKIPPED]: statusEntry('cancelled', 'Skipped'),
};

// User Attestation status type (workflow state for attestation submissions)
export const USER_ATTESTATION_STATUSES = {
    SENT: 'SENT',
    RECEIVED: 'RECEIVED',
    OVERDUE: 'OVERDUE',
    PASSED: 'PASSED',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
} as const;

export type UserAttestationStatus = (typeof USER_ATTESTATION_STATUSES)[keyof typeof USER_ATTESTATION_STATUSES];

export const USER_ATTESTATION_STATUS_CONFIG: Record<UserAttestationStatus, StatusConfig> = {
    [USER_ATTESTATION_STATUSES.SENT]: statusEntry('pending', 'Sent to user'),
    // Received waits on the reviewer, so it is `attention`, not more work in flight.
    [USER_ATTESTATION_STATUSES.RECEIVED]: statusEntry('attention', 'Awaiting review'),
    [USER_ATTESTATION_STATUSES.OVERDUE]: statusEntry('breached', 'Overdue'),
    [USER_ATTESTATION_STATUSES.PASSED]: statusEntry('succeeded', 'Passed'),
    [USER_ATTESTATION_STATUSES.FAILED]: statusEntry('failed', 'Failed'),
    [USER_ATTESTATION_STATUSES.CANCELLED]: statusEntry('cancelled', 'Cancelled'),
};

// Scheduled attestations (CUR-858): Active / Disabled / Completed (a spent one-off).
export const USER_ATTESTATION_SCHEDULE_STATUSES = {
    ACTIVE: 'ACTIVE',
    DISABLED: 'DISABLED',
    COMPLETED: 'COMPLETED',
} as const satisfies Record<ScheduledUserAttestationStatus, ScheduledUserAttestationStatus>;

export const USER_ATTESTATION_SCHEDULE_STATUS_CONFIG: Record<ScheduledUserAttestationStatus, StatusConfig> = {
    [USER_ATTESTATION_SCHEDULE_STATUSES.ACTIVE]: statusEntry('open', 'Active'),
    [USER_ATTESTATION_SCHEDULE_STATUSES.DISABLED]: statusEntry('idle', 'Disabled'),
    [USER_ATTESTATION_SCHEDULE_STATUSES.COMPLETED]: statusEntry('closed', 'Completed'),
};

// Attestation reporting: where a round (one send of one form) stands. Derived
// on the server from its rows — In progress while anything is outstanding and
// not past due, Past due once an outstanding row's due day has ended, Complete
// when nothing is left for the recipient or the reviewer.
export const ATTESTATION_ROUND_STATUSES = {
    IN_PROGRESS: 'IN_PROGRESS',
    PAST_DUE: 'PAST_DUE',
    COMPLETE: 'COMPLETE',
} as const;

export type AttestationRoundStatus = (typeof ATTESTATION_ROUND_STATUSES)[keyof typeof ATTESTATION_ROUND_STATUSES];

export const ATTESTATION_ROUND_STATUS_CONFIG: Record<AttestationRoundStatus, StatusConfig> = {
    [ATTESTATION_ROUND_STATUSES.IN_PROGRESS]: statusEntry('pending', 'In progress'),
    [ATTESTATION_ROUND_STATUSES.PAST_DUE]: statusEntry('breached', 'Past due'),
    [ATTESTATION_ROUND_STATUSES.COMPLETE]: statusEntry('closed', 'Complete'),
};

// Form Schema status type (workflow state for attestation form schemas)
export const FORM_SCHEMA_STATUSES = {
    DRAFT: 'DRAFT',
    PUBLISHED: 'PUBLISHED',
    ARCHIVED: 'ARCHIVED',
} as const;

export type FormSchemaStatus = (typeof FORM_SCHEMA_STATUSES)[keyof typeof FORM_SCHEMA_STATUSES];

export const FORM_SCHEMA_STATUS_CONFIG: Record<FormSchemaStatus, StatusConfig> = {
    [FORM_SCHEMA_STATUSES.DRAFT]: statusEntry('idle', 'Draft'),
    [FORM_SCHEMA_STATUSES.PUBLISHED]: statusEntry('open', 'Published'),
    [FORM_SCHEMA_STATUSES.ARCHIVED]: statusEntry('closed', 'Archived'),
};

// PDS Processing status type (workflow state for Product Disclosure Statement processing)
export const PDS_PROCESSING_STATUSES = {
    PENDING: 'PENDING',
    PROCESSING: 'PROCESSING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
} as const;

export type PdsProcessingStatus = (typeof PDS_PROCESSING_STATUSES)[keyof typeof PDS_PROCESSING_STATUSES];

export const PDS_PROCESSING_STATUS_CONFIG: Record<PdsProcessingStatus, StatusConfig> = {
    [PDS_PROCESSING_STATUSES.PENDING]: statusEntry('idle', 'Pending'),
    [PDS_PROCESSING_STATUSES.PROCESSING]: statusEntry('pending', 'Processing'),
    [PDS_PROCESSING_STATUSES.COMPLETED]: statusEntry('succeeded', 'Completed'),
    [PDS_PROCESSING_STATUSES.FAILED]: statusEntry('failed', 'Failed'),
};

// Extraction status type (workflow state for document extraction)
export const EXTRACTION_STATUSES = {
    PENDING: 'PENDING',
    PROCESSING: 'PROCESSING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    SKIPPED: 'SKIPPED',
    CANCELLED: 'CANCELLED',
} as const;

export type ExtractionStatusType = (typeof EXTRACTION_STATUSES)[keyof typeof EXTRACTION_STATUSES];

export const EXTRACTION_STATUS_CONFIG: Record<ExtractionStatusType, StatusConfig> = {
    [EXTRACTION_STATUSES.PENDING]: statusEntry('idle', 'Pending'),
    [EXTRACTION_STATUSES.PROCESSING]: statusEntry('pending', 'Processing'),
    [EXTRACTION_STATUSES.COMPLETED]: statusEntry('succeeded', 'Processed'),
    [EXTRACTION_STATUSES.FAILED]: statusEntry('failed', 'Failed'),
    [EXTRACTION_STATUSES.SKIPPED]: statusEntry('cancelled', 'Skipped'),
    [EXTRACTION_STATUSES.CANCELLED]: statusEntry('cancelled', 'Cancelled'),
};

// Compliance audit status config (CUR-168). One vocabulary end to end (see the
// align-compliance-audit-status-vocabulary change): PENDING absorbed the old
// IN_PROGRESS *and* RUNNING, and BLOCKED — which never had a writer — is gone. A
// step-level QUERIED derives the audit to WAITING, so there is no audit-level
// query status. CUR-1624 — a finished audit carries its verdict: COMPLETED became
// PASSED / FAILED, labelled Pass / Fail to match the step rail directly below the
// details header. ERROR stays a red "Error" (a technical failure, told apart from
// a red Fail by label alone, as at step level).
export const COMPLIANCE_AUDIT_STATUS_CONFIG: Record<ComplianceAuditStatus, StatusConfig> = {
    [ComplianceAuditStatus.NOT_STARTED]: statusEntry('idle', 'Not started'),
    [ComplianceAuditStatus.PENDING]: statusEntry('pending', 'In progress'),
    [ComplianceAuditStatus.WAITING]: statusEntry('attention', 'Waiting'),
    [ComplianceAuditStatus.PASSED]: statusEntry('succeeded', 'Pass'),
    [ComplianceAuditStatus.FAILED]: statusEntry('failed', 'Fail'),
    [ComplianceAuditStatus.ERROR]: statusEntry('failed', 'Error'),
};

// Compliance audit STEP status config (CUR-168 PR3). Terminal verdicts read as the
// authored vocabulary — Pass / Fail / Query / Not applicable.
export const COMPLIANCE_AUDIT_STEP_STATUS_CONFIG: Record<ComplianceAuditStepStatus, StatusConfig> = {
    [ComplianceAuditStepStatus.NOT_STARTED]: statusEntry('idle', 'Not started'),
    [ComplianceAuditStepStatus.PENDING]: statusEntry('pending', 'Running'),
    // Halted *before* the verdict on an unanswered sub-step question — the auditor
    // answers in the sub-step's thread and the chain resumes.
    [ComplianceAuditStepStatus.WAITING]: statusEntry('attention', 'Waiting'),
    // The step decision itself returned QUERY: halted *after* the sub-steps, with a
    // question for the auditor and no verdict. Deliberately not the amber `attention`
    // treatment of WAITING — the two mean different halts and can be adjacent in the
    // step rail, so QUERIED has its own role (violet) and its own "Query" label.
    [ComplianceAuditStepStatus.QUERIED]: statusEntry('queried', 'Query'),
    [ComplianceAuditStepStatus.PASSED]: statusEntry('succeeded', 'Pass'),
    [ComplianceAuditStepStatus.FAILED]: statusEntry('failed', 'Fail'),
    // Technical failure (CUR-168 §4.4) — distinct from FAILED (a rule verdict); the
    // "Error" label differentiates it from a red Fail badge.
    [ComplianceAuditStepStatus.ERROR]: statusEntry('failed', 'Error'),
    // CUR-551 — a step routed out by an unmet `appliesWhen`: resolved, but neither a
    // green Pass nor a red Fail. Neutral `cancelled`, deliberately not `succeeded`.
    // The grey collision with NOT_STARTED is acceptable: the two never co-occur in a
    // completed audit (NOT_STARTED means un-run; NOT_APPLICABLE is a resolved outcome).
    [ComplianceAuditStepStatus.NOT_APPLICABLE]: statusEntry('cancelled', 'Not applicable'),
};

// CommTemplate / comm-template versioning status (DRAFT → PUBLISHED → ARCHIVED).
export const COMM_TEMPLATE_STATUS_CONFIG: Record<CommTemplateStatus, StatusConfig> = {
    [CommTemplateStatus.DRAFT]: statusEntry('idle', 'Draft', Pencil),
    [CommTemplateStatus.PUBLISHED]: statusEntry('open', 'Published', CircleCheck),
    [CommTemplateStatus.ARCHIVED]: statusEntry('closed', 'Archived', Archive),
};

// Vehicle inspection status type (workflow state for a Ravin AI damage scan)
export const VEHICLE_INSPECTION_STATUS_CONFIG: Record<
    VehicleInspectionStatus | 'LINK_EXPIRED' | 'SUPERSEDED',
    StatusConfig
> = {
    // Both "awaiting inspection" and "processing" are work in flight, so both are `pending`;
    // the label carries the stage.
    [VehicleInspectionStatus.LINK_REQUESTED]: statusEntry('pending', 'Scan link issued — awaiting inspection'),
    // Derived, not a stored status: a LINK_REQUESTED row whose capture link ran out with no report.
    // The provider sends nothing for it either way, so a person has to check with the claimant and
    // perhaps send a new link — `attention`, not `pending`. A late report still lands normally.
    LINK_EXPIRED: statusEntry('attention', 'Scan link expired — no report received'),
    // Derived too: an expired or overdue run a later inspection on the claim has followed. It
    // ended without a report and asks nothing more of anyone: `cancelled`, ended without an outcome.
    SUPERSEDED: statusEntry('cancelled', 'Superseded — no report received'),
    [VehicleInspectionStatus.REPORT_RECEIVED]: statusEntry('pending', 'Report received — processing'),
    [VehicleInspectionStatus.PROCESSING]: statusEntry('pending', 'Report received — processing'),
    [VehicleInspectionStatus.PROCESSED]: statusEntry('succeeded', 'Report processed'),
    [VehicleInspectionStatus.FAILED]: statusEntry('failed', 'Report processing failed'),
};

export const CLAIM_AUTHORISATION_STATUS_CONFIG = {
    APPROVED: statusEntry('succeeded', 'Authorised'),
    PENDING: statusEntry('attention', 'Awaiting authorisation'),
    DECLINED: statusEntry('failed', 'Declined'),
    SUPERSEDED: statusEntry('cancelled', 'Superseded'),
    AWAITING_QUOTE: statusEntry('pending', 'Awaiting quote'),
} as const satisfies Record<string, StatusConfig>;

export const DOMAIN_AUTH_STATUS_CONFIG = {
    verified: statusEntry('succeeded', 'Verified'),
    pending: statusEntry('attention', 'Pending'),
    failed: statusEntry('failed', 'Failed'),
} as const satisfies Record<string, StatusConfig>;

/**
 * Training progress against a period's hours/CPD target. A workflow state, not a
 * fixed attribute — it flips as records are logged and as the period runs down,
 * so it belongs here rather than in per-card colour maps.
 */
export const TRAINING_STATUS_CONFIG = {
    [STAFF_TRAINING_STATUSES.ON_TRACK]: statusEntry('open', 'On track', CheckCircle2),
    [STAFF_TRAINING_STATUSES.BEHIND]: statusEntry('breached', 'Off target', AlertTriangle),
    [STAFF_TRAINING_STATUSES.AHEAD]: statusEntry('succeeded', 'Ahead', TrendingUp),
} as const satisfies Record<StaffTrainingStatus, StatusConfig>;

export const VEHICLE_VERIFICATION_STATUS_CONFIG = {
    VERIFIED: statusEntry('succeeded', FLEET_VEHICLE_VERIFICATION_STATUS_LABELS.VERIFIED, ShieldCheck),
    UNVERIFIED: statusEntry('idle', FLEET_VEHICLE_VERIFICATION_STATUS_LABELS.UNVERIFIED, ShieldQuestion),
    NEEDS_VERIFICATION: statusEntry(
        'attention',
        FLEET_VEHICLE_VERIFICATION_STATUS_LABELS.NEEDS_VERIFICATION,
        ShieldAlert,
    ),
} as const satisfies Record<FleetVehicleVerificationStatus, StatusConfig>;

// MSP labels are organisation-configurable. Only these seeded lifecycle labels
// carry workflow colours; arbitrary customer text always uses the neutral fallback.
export const MSP_STATUS_CONFIG = {
    Onboarding: statusEntry('pending', 'Onboarding'),
    'Assessment in progress': statusEntry('pending', 'Assessment in progress'),
    'Accepted Risk': statusEntry('open', 'Accepted Risk'),
    'Ongoing Monitoring': statusEntry('open', 'Ongoing Monitoring'),
    Terminated: statusEntry('closed', 'Terminated'),
    unknown: statusEntry('idle', 'Unknown'),
} as const satisfies Record<string, StatusConfig>;

// Authored content that moves draft → published → archived (release notes, market PDS, AI prompt
// and communication template versions). PROCESSING is the market PDS extraction step.
export const PUBLICATION_STATUS_CONFIG = {
    DRAFT: statusEntry('idle', 'Draft'),
    PROCESSING: statusEntry('pending', 'Processing'),
    PUBLISHED: statusEntry('open', 'Published'),
    ARCHIVED: statusEntry('closed', 'Archived'),
} as const satisfies Record<
    AiPromptStatus | CommTemplateStatus | FaqStatus | MarketPdsStatus | WhatsNewReleaseStatus,
    StatusConfig
>;

// Eval dataset cases (CUR-1766): a draft awaits review, an approved case is live in the dataset
// and the next frozen version, and a retired one is out of every later version.
export const AI_EVAL_CASE_STATUS_CONFIG = {
    DRAFT: statusEntry('idle', 'Draft'),
    APPROVED: statusEntry('open', 'Approved'),
    RETIRED: statusEntry('closed', 'Retired'),
} as const satisfies Record<AiEvalCaseStatus, StatusConfig>;

// Background runs and their per-case results (AI eval runs).
export const RUN_STATUS_CONFIG = {
    PENDING: statusEntry('idle', 'Pending'),
    RUNNING: statusEntry('pending', 'Running'),
    COMPLETED: statusEntry('succeeded', 'Completed'),
    PASSED: statusEntry('succeeded', 'Passed'),
    FAILED: statusEntry('failed', 'Failed'),
    ERROR: statusEntry('failed', 'Error'),
    CANCELLED: statusEntry('cancelled', 'Cancelled'),
} as const satisfies Record<AiEvalResultStatus | AiEvalRunStatus, StatusConfig>;

// AI organisation digest reports (admin console digest history). In-flight work is pending, a
// generated report waiting on its email needs attention, and a skipped week ended without a send.
export const ORG_DIGEST_REPORT_STATUS_CONFIG = {
    pending: statusEntry('idle', 'Pending'),
    analysing: statusEntry('pending', 'Analysing'),
    generated: statusEntry('attention', 'Generated'),
    sending: statusEntry('pending', 'Sending'),
    sent: statusEntry('succeeded', 'Sent'),
    skipped: statusEntry('closed', 'Skipped'),
    failed: statusEntry('failed', 'Failed'),
    email_failed: statusEntry('failed', 'Email failed'),
} as const satisfies Record<OrgDigestReportStatus, StatusConfig>;

// Claim excess. Waived and cancelled both end the excess without a settlement, so both are neutral.
export const EXCESS_STATUS_CONFIG = {
    // Nothing has been received yet, so the excess is still owed: someone has to collect it.
    UNRESOLVED: statusEntry('attention', 'Unresolved'),
    PENDING: statusEntry('pending', 'Pending'),
    PARTIALLY_SETTLED: statusEntry('attention', 'Partially settled'),
    FULLY_SETTLED: statusEntry('succeeded', 'Fully settled'),
    WAIVED: statusEntry('cancelled', 'Waived'),
    CANCELLED: statusEntry('cancelled', 'Cancelled'),
} as const satisfies Record<ExcessStatus, StatusConfig>;

// Website checker (CUR-1337): scan lifecycle and per-finding verdicts. Labels vary by surface
// ("Present" on a card header, "Likely pass" on a sub-check), so callers pass `customLabel`.
export const WEBSITE_SCAN_STATUS_CONFIG = {
    PENDING: statusEntry('idle', COMPLIANCE_SCAN_STATUS_LABELS.PENDING),
    SCRAPING: statusEntry('pending', COMPLIANCE_SCAN_STATUS_LABELS.SCRAPING),
    SCRAPE_FAILED: statusEntry('failed', COMPLIANCE_SCAN_STATUS_LABELS.SCRAPE_FAILED),
    ANALYZING: statusEntry('pending', COMPLIANCE_SCAN_STATUS_LABELS.ANALYZING),
    COMPLETED: statusEntry('succeeded', COMPLIANCE_SCAN_STATUS_LABELS.COMPLETED),
    FAILED: statusEntry('failed', COMPLIANCE_SCAN_STATUS_LABELS.FAILED),
    CANCELLED: statusEntry('cancelled', COMPLIANCE_SCAN_STATUS_LABELS.CANCELLED),
} as const satisfies Record<ComplianceScanStatus, StatusConfig>;

export const COMPLIANCE_FINDING_STATUS_CONFIG = {
    PASS: statusEntry('succeeded', 'Pass'),
    FAIL: statusEntry('failed', 'Fail'),
    MANUAL_REVIEW: statusEntry('attention', 'Unclear'),
    PENDING: statusEntry('pending', 'Pending'),
    // The AI may return NOT_APPLICABLE for a rule that does not apply to the site.
    NOT_APPLICABLE: statusEntry('cancelled', 'Not applicable'),
    ERROR: statusEntry('failed', 'Error'),
} as const satisfies Record<CheckStatus, StatusConfig>;

// LLM request outcomes (admin LLM logs, AI feedback, file-audit trace). The column is a string the
// server only writes from `LlmRequestStatus`. A timeout and a rate limit are both a request that
// returned nothing usable, so both are `failed`; the label says which.
export const LLM_REQUEST_STATUS_CONFIG = {
    SUCCESS: statusEntry('succeeded', 'Success'),
    ERROR: statusEntry('failed', 'Error'),
    TIMEOUT: statusEntry('failed', 'Timeout'),
    RATE_LIMITED: statusEntry('failed', 'Rate limited'),
} as const satisfies Record<string, StatusConfig>;

// Heap snapshot exports to object storage (admin memory monitor).
export const HEAP_SNAPSHOT_EXPORT_STATUS_CONFIG = {
    uploading: statusEntry('pending', 'Uploading'),
    done: statusEntry('succeeded', 'Done'),
    failed: statusEntry('failed', 'Failed'),
} as const satisfies Record<string, StatusConfig>;

// Inbound email archive: RECEIVED is waiting on the processor; REJECTED is a refused message.
export const INBOUND_EMAIL_ARCHIVE_STATUS_CONFIG = {
    RECEIVED: statusEntry('pending', 'Received'),
    PROCESSED: statusEntry('succeeded', 'Processed'),
    REJECTED: statusEntry('failed', 'Rejected'),
    PARSE_FAILED: statusEntry('failed', 'Parse failed'),
    PROCESSING_FAILED: statusEntry('failed', 'Processing failed'),
} as const satisfies Record<InboundEmailArchiveStatus, StatusConfig>;

// Contact vulnerability flags: an active flag tells staff to take extra care, so it is `attention`
// rather than a healthy green `open`. Resolving or expiring a flag ends it normally (neutral);
// dismissing it ends it without an outcome.
export const CONTACT_VULNERABILITY_FLAG_STATUS_CONFIG = {
    ACTIVE: statusEntry('attention', 'Active'),
    EXPIRED: statusEntry('closed', 'Expired'),
    RESOLVED: statusEntry('closed', 'Resolved'),
    DISMISSED: statusEntry('cancelled', 'Dismissed'),
} as const satisfies Record<ContactVulnerabilityFlagStatus, StatusConfig>;

// Vulnerability signals detected on an inbound message. DETECTED waits on a reviewer; PROMOTED has
// been handed over to a contact vulnerability flag, which ends the signal's own lifecycle.
export const MESSAGE_VULNERABILITY_STATUS_CONFIG = {
    DETECTED: statusEntry('attention', 'Detected'),
    CONFIRMED: statusEntry('succeeded', 'Confirmed'),
    DISMISSED: statusEntry('cancelled', 'Dismissed'),
    PROMOTED: statusEntry('closed', 'Promoted'),
} as const satisfies Record<MessageVulnerabilityStatus, StatusConfig>;

// Third-party vehicle assessment requests (claims and the fleet portal).
export const VEHICLE_ASSESSMENT_STATUS_CONFIG = {
    REQUEST_SENT: statusEntry('pending', VEHICLE_ASSESSMENT_STATUS_LABELS.REQUEST_SENT),
    REQUEST_FAILED: statusEntry('failed', VEHICLE_ASSESSMENT_STATUS_LABELS.REQUEST_FAILED),
    CREATED: statusEntry('pending', VEHICLE_ASSESSMENT_STATUS_LABELS.CREATED),
    CLOSED: statusEntry('closed', VEHICLE_ASSESSMENT_STATUS_LABELS.CLOSED),
} as const satisfies Record<VehicleAssessmentStatus, StatusConfig>;

// AI agent assessment runs (windscreen, tyre). PARTIAL returned a result a person should check.
export const AGENT_ASSESSMENT_STATUS_CONFIG = {
    COMPLETED: statusEntry('succeeded', 'Completed'),
    FAILED: statusEntry('failed', 'Failed'),
    PARTIAL: statusEntry('attention', 'Partial'),
} as const satisfies Record<AgentAssessmentResult['status'], StatusConfig>;

// Support access sessions: a pending request waits on the organisation's approval.
export const SUPPORT_SESSION_STATUS_CONFIG = {
    pending: statusEntry('attention', SUPPORT_SESSION_STATUS_LABELS.pending),
    approved: statusEntry('open', SUPPORT_SESSION_STATUS_LABELS.approved),
    rejected: statusEntry('failed', SUPPORT_SESSION_STATUS_LABELS.rejected),
    expired: statusEntry('closed', SUPPORT_SESSION_STATUS_LABELS.expired),
    revoked: statusEntry('failed', SUPPORT_SESSION_STATUS_LABELS.revoked),
} as const satisfies Record<SupportSessionStatus, StatusConfig>;

// Rows of the admin CSV import wizards (users, incidents, complaints, controls, risks, training
// records, FAQs, comm templates): the preview verdict for each row and the summary counts beside it.
// `new` and `update` say what the import will do with a valid row; an `exists` row is skipped.
export const IMPORT_ROW_STATUS_CONFIG = {
    valid: statusEntry('succeeded', 'Valid'),
    invalid: statusEntry('failed', 'Invalid'),
    error: statusEntry('failed', 'Error'),
    new: statusEntry('open', 'New'),
    exists: statusEntry('attention', 'Exists'),
    update: statusEntry('pending', 'Update'),
    // Master obligation import: a row that matches the current version, and one that will mint a
    // new version of an existing obligation (a person should check what it overrides).
    unchanged: statusEntry('idle', 'No change'),
    'new-version': statusEntry('attention', 'New version'),
} as const satisfies Record<string, StatusConfig>;

// Weaviate v1 decommission state machine (admin console, CUR-862): where the cleanup stands. Ready and
// partial wait on an operator; completed is a success, never a red blocker.
export const WEAVIATE_CLEANUP_STATUS_CONFIG = {
    'not-ready': statusEntry('idle', 'Not ready'),
    ready: statusEntry('attention', 'Ready'),
    running: statusEntry('pending', 'Running'),
    partial: statusEntry('attention', 'Partially completed'),
    completed: statusEntry('succeeded', 'Completed'),
    failed: statusEntry('failed', 'Failed'),
} as const satisfies Record<RouterOutputs['admin']['weaviate']['getCleanupStatus']['state'], StatusConfig>;

// Per-collection result of the exact v1-vs-v2 identity checkpoint; unknown and missing mean the
// comparison could not be made for that collection and need a look.
export const WEAVIATE_IDENTITY_CHECK_STATUS_CONFIG = {
    pass: statusEntry('succeeded', 'Pass'),
    fail: statusEntry('failed', 'Fail'),
    unknown: statusEntry('attention', 'Unknown'),
    missing: statusEntry('attention', 'Missing'),
} as const satisfies Record<string, StatusConfig>;

// Weaviate client circuit breaker: CLOSED is the healthy path, OPEN is tripped, HALF_OPEN is probing.
export const CIRCUIT_BREAKER_STATUS_CONFIG = {
    CLOSED: statusEntry('open', 'Healthy', CheckCircle2),
    OPEN: statusEntry('failed', 'Circuit open', XCircle),
    HALF_OPEN: statusEntry('attention', 'Half-open', Clock),
} as const satisfies Record<string, StatusConfig>;

// Re-embed and collection backfill campaigns (Weaviate sync engine): the derived kind of the campaign.
// Idle means active between scheduled ticks, so it is still work in flight.
export const SYNC_CAMPAIGN_STATUS_CONFIG = {
    'never-run': statusEntry('idle', 'Never run', Clock),
    running: statusEntry('pending', 'Running now', RefreshCw),
    idle: statusEntry('pending', 'Active — between ticks', Clock),
    stalled: statusEntry('attention', 'Stalled', AlertTriangle),
    blocked: statusEntry('failed', 'Blocked', Ban),
    failed: statusEntry('failed', 'Failed', XCircle),
    complete: statusEntry('succeeded', 'Complete', CheckCircle2),
} as const satisfies Record<
    RouterOutputs['admin']['weaviate']['getSyncStatus']['reembed']['status']['kind'],
    StatusConfig
>;

// Whether a quarantined Weaviate mirror row still has its Postgres source row.
export const POSTGRES_SOURCE_ROW_STATUS_CONFIG = {
    exists: statusEntry('succeeded', 'Source exists', CheckCircle2),
    missing: statusEntry('failed', 'Orphan', AlertTriangle),
    unknown: statusEntry('idle', 'Unknown', HelpCircle),
} as const satisfies Record<string, StatusConfig>;

// MX record check for an inbound parse hostname: whether it points at SendGrid.
export const MX_RECORD_STATUS_CONFIG = {
    configured: statusEntry('succeeded', 'Valid', CheckCircle2),
    misconfigured: statusEntry('attention', 'Wrong target', AlertTriangle),
    error: statusEntry('attention', 'Retry', AlertTriangle),
    missing: statusEntry('failed', 'Missing', XCircle),
} as const satisfies Record<string, StatusConfig>;

// Overall health of an inbound parse hostname (MX plus SendGrid setting); unknown could not be checked.
export const INBOUND_PARSE_HEALTH_STATUS_CONFIG = {
    healthy: statusEntry('open', 'Healthy', CheckCircle2),
    degraded: statusEntry('attention', 'Degraded', AlertTriangle),
    misconfigured: statusEntry('failed', 'Misconfigured', XCircle),
    unknown: statusEntry('attention', 'Unknown', AlertTriangle),
} as const satisfies Record<string, StatusConfig>;

// Data-seeding db-upgrade run status (`PrismaJson.DbUpgradeStatus`); retry means a serialisation
// conflict stopped the run and it will be attempted again.
export const DB_UPGRADE_STATUS_CONFIG = {
    pending: statusEntry('idle', 'Pending'),
    completed: statusEntry('succeeded', 'Completed'),
    failed: statusEntry('failed', 'Failed'),
    retry: statusEntry('attention', 'Retry'),
} as const satisfies Record<string, StatusConfig>;

// How one test case changed between two AI eval runs (A → B).
export const EVAL_RESULT_CHANGE_STATUS_CONFIG = {
    NEW_IN_B: statusEntry('idle', 'New in B'),
    ONLY_IN_A: statusEntry('attention', 'Only in A'),
    UNCHANGED: statusEntry('closed', 'Unchanged'),
    IMPROVED: statusEntry('succeeded', 'Improved'),
    REGRESSION: statusEntry('failed', 'Regression'),
    CHANGED: statusEntry('attention', 'Changed'),
} as const satisfies Record<string, StatusConfig>;

// Compliance monitoring master tracking list: whether a metric is implemented in the product.
export const MASTER_TRACKING_IMPLEMENTATION_STATUS_CONFIG = {
    implemented: statusEntry('succeeded', 'Yes'),
    partial: statusEntry('attention', 'Partial'),
    'not-implemented': statusEntry('failed', 'No'),
} as const satisfies Record<string, StatusConfig>;

// Live-updates test page (admin console): the state of one server-sent-event subscription.
// `idle`, `connecting`, `pending` and `error` are tRPC's subscription states; `finished` is ours.
export const LIVE_CONNECTION_STATUS_CONFIG = {
    idle: statusEntry('idle', 'Not connected'),
    connecting: statusEntry('pending', 'Connecting'),
    pending: statusEntry('open', 'Live'),
    error: statusEntry('failed', 'Error'),
    finished: statusEntry('closed', 'Finished'),
} as const satisfies Record<string, StatusConfig>;

// Result of one `checkSubsystems` probe (DB, Redis, queues, disk, memory) and the overall roll-up.
export const SUBSYSTEM_CHECK_STATUS_CONFIG = {
    success: statusEntry('open', 'Healthy'),
    warning: statusEntry('attention', 'Warning'),
    failed: statusEntry('failed', 'Failed'),
} as const satisfies Record<
    RouterOutputs['admin']['monitor']['checkSubsystems']['checks'][number]['status'],
    StatusConfig
>;

// A user's Auth0 account as the admin console sees it: whether it exists and whether it is blocked.
export const AUTH0_ACCOUNT_STATUS_CONFIG = {
    connected: statusEntry('open', 'Connected'),
    notFound: statusEntry('attention', 'Not found'),
    active: statusEntry('open', 'Active'),
    blocked: statusEntry('failed', 'Blocked'),
} as const satisfies Record<string, StatusConfig>;

// Where a user is in the invite → signup → terms journey (admin console user drawer). Expired and
// not-registered wait on someone to resend or chase; blocked is refused in Auth0.
export const REGISTRATION_JOURNEY_STATUS_CONFIG = {
    pending: statusEntry('pending', 'Invite pending'),
    expired: statusEntry('attention', 'Invite expired'),
    not_registered: statusEntry('attention', 'Not registered'),
    blocked: statusEntry('failed', 'Account blocked'),
    terms_pending: statusEntry('attention', 'Terms not accepted'),
    active: statusEntry('open', 'Fully registered'),
} as const satisfies Record<string, StatusConfig>;

// Terms and conditions acceptance, per organisation or rolled up across a user's organisations.
export const TERMS_ACCEPTANCE_STATUS_CONFIG = {
    accepted: statusEntry('succeeded', 'Accepted'),
    partial: statusEntry('attention', 'Partially accepted'),
    pending: statusEntry('idle', 'Pending'),
    notAccepted: statusEntry('failed', 'Not accepted'),
} as const satisfies Record<string, StatusConfig>;

// Summary status of a user account in the admin console header, derived from Auth0, invite and login facts.
export const USER_ACCOUNT_STATUS_CONFIG = {
    notInAuth0: statusEntry('attention', 'Not in Auth0'),
    invited: statusEntry('pending', 'Invited'),
    inviteExpired: statusEntry('attention', 'Invite expired'),
    notRegistered: statusEntry('attention', 'Not registered'),
    emailUnverified: statusEntry('attention', 'Email unverified'),
    blocked: statusEntry('failed', 'Blocked'),
    active: statusEntry('open', 'Active'),
    registered: statusEntry('idle', 'Registered'),
} as const satisfies Record<string, StatusConfig>;

// One organisation's invitation to an identity that has not signed up (CUR-1208, admin console user
// drawer). `EXPIRED` is not stored: the client derives it for a PENDING invitation past its expiry,
// which waits on the organisation to resend. Accepting one invitation supersedes the identity's
// other pending ones (ended without an outcome); disabling the identity revokes them (refused).
export const USER_INVITATION_STATUS_CONFIG = {
    PENDING: statusEntry('pending', 'Pending'),
    EXPIRED: statusEntry('attention', 'Expired'),
    ACCEPTED: statusEntry('succeeded', 'Accepted'),
    SUPERSEDED: statusEntry('cancelled', 'Superseded'),
    REVOKED: statusEntry('failed', 'Revoked'),
} as const satisfies Record<UserInvitationStatus | 'EXPIRED', StatusConfig>;

// Outcome of one Auth0 authentication event (sign-in, signup, MFA…): the event label is the badge text.
export const AUTH_EVENT_STATUS_CONFIG = {
    success: statusEntry('succeeded', 'Success'),
    failure: statusEntry('failed', 'Failure'),
    info: statusEntry('idle', 'Info'),
} as const satisfies Record<string, StatusConfig>;

// Last Auth0 profile sync result for a user (`User.lastSyncStatus`); never means no sync has run.
export const AUTH0_PROFILE_SYNC_STATUS_CONFIG = {
    success: statusEntry('succeeded', 'Success'),
    error: statusEntry('failed', 'Error'),
    never: statusEntry('idle', 'Never synced'),
} as const satisfies Record<string, StatusConfig>;

// Reviewer verdict on an AI obligation finding: confirmed stands, dismissed ends the finding with no action.
export const AI_FINDING_REVIEW_STATUS_CONFIG = {
    CONFIRMED: statusEntry('succeeded', 'Confirmed'),
    DISMISSED: statusEntry('cancelled', 'Dismissed'),
} as const satisfies Record<string, StatusConfig>;

// Fleet portal user access: an invitation is in flight until the user accepts; revoking access is a
// refused state. `NO_ROLE` is a membership row the portal procedures will refuse (missing portal role).
export const PORTAL_USER_STATUS_CONFIG = {
    ACTIVE: statusEntry('open', 'Active'),
    INVITED: statusEntry('pending', 'Invited'),
    REVOKED: statusEntry('failed', 'Revoked'),
    NO_ROLE: statusEntry('failed', 'No portal role'),
} as const satisfies Record<
    RouterOutputs['fleetClientDb']['listPortalUsers']['rows'][number]['status'] | 'NO_ROLE',
    StatusConfig
>;

// Fleet portal SLA rows: performance against a contractual target. `watch` is drifting and needs a
// person to look; `exceed` is beating the target.
export const FLEET_SLA_STATUS_CONFIG = {
    ok: statusEntry('open', 'On target'),
    watch: statusEntry('attention', 'Watch'),
    exceed: statusEntry('succeeded', 'Exceeding'),
} as const satisfies Record<string, StatusConfig>;

// Outcome of the most recent test of a risk control; `NOT_TESTED` is a control with no test yet.
export const CONTROL_TEST_STATUS_CONFIG = {
    PASS: statusEntry('succeeded', 'Pass'),
    FAIL: statusEntry('failed', 'Fail'),
    NOT_TESTED: statusEntry('idle', 'Not tested'),
} as const satisfies Record<ControlTestOutcome | 'NOT_TESTED', StatusConfig>;

// Stripe invoice lifecycle as the billing page shows it. An open invoice is awaiting payment.
export const STRIPE_INVOICE_STATUS_CONFIG = {
    draft: statusEntry('idle', 'Draft'),
    open: statusEntry('attention', 'Open'),
    paid: statusEntry('succeeded', 'Paid'),
    uncollectible: statusEntry('failed', 'Uncollectible'),
    void: statusEntry('cancelled', 'Void'),
} as const satisfies Record<string, StatusConfig>;

// Website checker: the overall verdict of a completed compliance scan.
export const COMPLIANCE_OVERALL_RESULT_CONFIG = {
    COMPLIANT: statusEntry('succeeded', OVERALL_RESULT_LABELS.COMPLIANT),
    NON_COMPLIANT: statusEntry('failed', OVERALL_RESULT_LABELS.NON_COMPLIANT),
    PARTIALLY_COMPLIANT: statusEntry('attention', OVERALL_RESULT_LABELS.PARTIALLY_COMPLIANT),
} as const satisfies Record<ComplianceOverallResult, StatusConfig>;

// Vehicle inspection triage verdict, keyed by the provider's numeric code (0 total loss, 1 repair,
// 2 borderline). Render with `status={String(triage)}`.
// Ravin's full triage set (CUR-1693). Verdicts 0 to 4 are results; 5, 6 and -1 say the provider
// could not assess: still calculating is work in flight, needs attention asks for a person, and
// missing data ended without an outcome.
export const VEHICLE_INSPECTION_TRIAGE_CONFIG = {
    '0': statusEntry('failed', VEHICLE_INSPECTION_TRIAGE_LABELS[0] ?? 'Total loss'),
    '1': statusEntry('succeeded', VEHICLE_INSPECTION_TRIAGE_LABELS[1] ?? 'Repair'),
    '2': statusEntry('attention', VEHICLE_INSPECTION_TRIAGE_LABELS[2] ?? 'Borderline repair'),
    '3': statusEntry('attention', VEHICLE_INSPECTION_TRIAGE_LABELS[3] ?? 'Borderline total loss'),
    '4': statusEntry('succeeded', VEHICLE_INSPECTION_TRIAGE_LABELS[4] ?? 'No damage'),
    '5': statusEntry('pending', VEHICLE_INSPECTION_TRIAGE_LABELS[5] ?? 'Calculation in progress'),
    '6': statusEntry('attention', VEHICLE_INSPECTION_TRIAGE_LABELS[6] ?? 'Needs attention'),
    '-1': statusEntry('idle', VEHICLE_INSPECTION_TRIAGE_LABELS[-1] ?? 'Missing data'),
} as const satisfies Record<string, StatusConfig>;

// Auto-detect inbox: the AI verdict on a message and a reviewer's per-obligation breach call, keyed
// by the classification pill key. `uncertain` is a verdict the AI could not make.
export const MESSAGE_CLASSIFICATION_VERDICT_CONFIG = {
    breach: statusEntry('breached', 'Breach'),
    noBreach: statusEntry('succeeded', 'No breach'),
    noIssue: statusEntry('succeeded', 'No issue detected'),
    uncertain: statusEntry('idle', 'Uncertain'),
} as const satisfies Record<string, StatusConfig>;

// Auto-detect inbox: a reviewer's decision on a claim the message was linked to.
export const LINKED_CLAIM_DECISION_CONFIG = {
    CONFIRM: statusEntry('succeeded', 'Confirmed'),
    REJECT: statusEntry('failed', 'Rejected'),
} as const satisfies Record<'CONFIRM' | 'REJECT', StatusConfig>;

// Claim decision: the tenant-configured outcome on a claim (`Claim.decision`, default "pending"). The
// stock values are pending, accepted and declined; a tenant may add its own (for example "partially
// accepted"), which `StatusBadge` renders as a neutral badge.
export const CLAIM_DECISION_CONFIG = {
    pending: statusEntry('pending', 'Pending'),
    accepted: statusEntry('succeeded', 'Accepted'),
    declined: statusEntry('failed', 'Declined'),
} as const satisfies Record<'pending' | 'accepted' | 'declined', StatusConfig>;

// Claim status type: the open/closed half of a tenant-configured claim status. Callers pass the
// tenant's status name as `customLabel`; the configured colour of that status is not used here.
export const CLAIM_STATUS_TYPE_CONFIG = {
    OPEN: statusEntry('open', 'Open'),
    CLOSED: statusEntry('closed', 'Closed'),
} as const satisfies Record<ClaimStatusType, StatusConfig>;

// MSPR governance check assessment result; a conditional pass carries conditions a person must follow up.
export const MSPR_GOVERNANCE_ASSESSMENT_STATUS_CONFIG = {
    PASS: statusEntry('succeeded', MSPR_GOVERNANCE_ASSESSMENT_RESULT_LABELS.PASS),
    CONDITIONAL_PASS: statusEntry('attention', MSPR_GOVERNANCE_ASSESSMENT_RESULT_LABELS.CONDITIONAL_PASS),
    FAIL: statusEntry('failed', MSPR_GOVERNANCE_ASSESSMENT_RESULT_LABELS.FAIL),
    NOT_ASSESSED: statusEntry('idle', 'Not Assessed'),
} as const satisfies Record<MsprGovernanceAssessmentResult | 'NOT_ASSESSED', StatusConfig>;

// Stages of a claim's repair journey (damage capture → quote → assessment → repair). A total loss
// verdict ends the journey early and needs the handler's attention; skipped and cancelled end it
// without an outcome.
export const REPAIR_STAGE_STATUS_CONFIG = {
    complete: statusEntry('succeeded', 'Complete'),
    current: statusEntry('pending', 'In progress'),
    upcoming: statusEntry('idle', 'Waiting'),
    total_loss: statusEntry('attention', 'Total loss'),
    cancelled: statusEntry('cancelled', 'Cancelled'),
    skipped: statusEntry('cancelled', 'Skipped'),
} as const satisfies Record<
    RouterOutputs['vehicleAssessment']['getRepairProgress']['stages'][number]['status'],
    StatusConfig
>;

// A repairer booked by hand when Clear21 could not take an automated quote request (CUR-1819): the
// handler has to phone or email the repairer until the booking task is completed or cancelled.
export const MANUAL_REPAIR_BOOKING_STATUS_CONFIG = {
    TO_BOOK: statusEntry('attention', 'To book'),
    BOOKED: statusEntry('succeeded', 'Booked manually'),
    CANCELLED: statusEntry('cancelled', 'Cancelled'),
} as const satisfies Record<
    NonNullable<RouterOutputs['vehicleAssessment']['getRepairProgress']['manualBooking']>['status'],
    StatusConfig
>;

// Personal access tokens (authorised apps): expiry ends the token normally, revocation refuses it.
export const PERSONAL_ACCESS_TOKEN_STATUS_CONFIG = {
    ACTIVE: statusEntry('open', 'Active'),
    EXPIRED: statusEntry('closed', 'Expired'),
    REVOKED: statusEntry('failed', 'Revoked'),
} as const satisfies Record<string, StatusConfig>;

// Outcome of one test within an admin-console integration test suite run.
export const INTEGRATION_TEST_RESULT_STATUS_CONFIG = {
    PASSED: statusEntry('succeeded', 'Passed'),
    FAILED: statusEntry('failed', 'Failed'),
    WARNING: statusEntry('attention', 'Warning'),
    DISABLED: statusEntry('idle', 'Disabled'),
    SKIPPED: statusEntry('cancelled', 'Skipped'),
} as const satisfies Record<IntegrationTestResultStatus, StatusConfig>;

// Background queue jobs (admin job monitor), derived from the BullMQ timestamps of the job.
export const JOB_STATUS_CONFIG = {
    pending: statusEntry('idle', 'Pending'),
    processing: statusEntry('pending', 'Processing'),
    completed: statusEntry('succeeded', 'Completed'),
    failed: statusEntry('failed', 'Failed'),
} as const satisfies Record<JobStatus, StatusConfig>;

// Sender email verification (outgoing email settings). The system domain needs no verification, so
// it is as healthy as a verified custom domain; pending and fallback both wait on domain authentication.
export const SENDER_EMAIL_VERIFICATION_STATUS_CONFIG = {
    system: statusEntry('open', 'System domain'),
    verified: statusEntry('succeeded', 'Verified'),
    pending: statusEntry('attention', 'Pending verification'),
    fallback: statusEntry('attention', 'Using system domain'),
} as const satisfies Record<string, StatusConfig>;

// Document analyser: the overall compliance verdict of an analysed document.
export const DOCUMENT_COMPLIANCE_STATUS_CONFIG = {
    COMPLIANT: statusEntry('succeeded', 'Compliant'),
    PARTIALLY_COMPLIANT: statusEntry('attention', 'Partially compliant'),
    NON_COMPLIANT: statusEntry('failed', 'Non-compliant'),
    UNCLEAR: statusEntry('attention', 'Unclear'),
} as const satisfies Record<string, StatusConfig>;

// AI-suggested flags on a complaint or incident: added to the record, or dismissed by the reviewer.
export const AI_SUGGESTED_FLAG_STATUS_CONFIG = {
    ADDED: statusEntry('succeeded', 'Added'),
    DISMISSED: statusEntry('cancelled', 'Dismissed'),
} as const satisfies Record<string, StatusConfig>;

// File audit trace: what one per-file scan of a verification round produced. A file that could
// not be read (or was withheld) needs the auditor's attention.
export const AUDIT_FILE_SCAN_OUTCOME_STATUS_CONFIG = {
    found: statusEntry('succeeded', 'Reported something'),
    silent: statusEntry('closed', 'Read, said nothing'),
    unavailable: statusEntry('attention', 'Could not be read'),
} as const satisfies Record<string, StatusConfig>;

// File audit trace: whether one execution of a sub-step ran to completion.
export const AUDIT_EXECUTION_STATUS_CONFIG = {
    COMPLETE: statusEntry('succeeded', 'Complete'),
    INCOMPLETE: statusEntry('attention', 'Incomplete'),
} as const satisfies Record<string, StatusConfig>;

// Document library rows: a soft-deleted file. A live file shows no badge.
export const FILE_RECORD_STATUS_CONFIG = {
    DELETED: statusEntry('closed', 'Deleted'),
} as const satisfies Record<string, StatusConfig>;

// Generic lifecycle and result states for the many admin and configuration screens that show one
// on/off, pass/fail or connected/offline fact and have no status enum of their own. A module named
// after the entity is better when its statuses are an enum; use this one for a boolean or an ad-hoc
// string rendered as a state, with `customLabel` when the surface needs other wording.
export const GENERIC_STATE_CONFIG = {
    ACTIVE: statusEntry('open', 'Active'),
    INACTIVE: statusEntry('closed', 'Inactive'),
    ARCHIVED: statusEntry('closed', 'Archived'),
    ENABLED: statusEntry('open', 'Enabled'),
    DISABLED: statusEntry('closed', 'Disabled'),
    ON: statusEntry('open', 'On'),
    OFF: statusEntry('closed', 'Off'),
    AVAILABLE: statusEntry('open', 'Available'),
    UNAVAILABLE: statusEntry('failed', 'Unavailable'),
    READY: statusEntry('open', 'Ready'),
    NOT_STARTED: statusEntry('idle', 'Not started'),
    IN_PROGRESS: statusEntry('pending', 'In progress'),
    COMPLETE: statusEntry('succeeded', 'Complete'),
    CANCELLED: statusEntry('cancelled', 'Cancelled'),
    EXPIRED: statusEntry('closed', 'Expired'),
    OVERDUE: statusEntry('breached', 'Overdue'),
    VERIFIED: statusEntry('succeeded', 'Verified'),
    UNVERIFIED: statusEntry('attention', 'Unverified'),
    MODIFIED: statusEntry('attention', 'Modified'),
    VALID: statusEntry('succeeded', 'Valid'),
    INVALID: statusEntry('failed', 'Invalid'),
    PASS: statusEntry('succeeded', 'Pass'),
    FAIL: statusEntry('failed', 'Fail'),
    NEW: statusEntry('open', 'New'),
    EXISTING: statusEntry('attention', 'Existing'),
    SKIPPED: statusEntry('cancelled', 'Skipped'),
    STORED: statusEntry('succeeded', 'Stored'),
    NOT_STORED: statusEntry('idle', 'Not stored'),
    CONNECTED: statusEntry('open', 'Connected'),
    DISCONNECTED: statusEntry('failed', 'Disconnected'),
    ONLINE: statusEntry('open', 'Online'),
    OFFLINE: statusEntry('failed', 'Offline'),
    HEALTHY: statusEntry('open', 'Healthy'),
    DEGRADED: statusEntry('attention', 'Degraded'),
    UNHEALTHY: statusEntry('failed', 'Unhealthy'),
    BLOCKED: statusEntry('failed', 'Blocked'),
    UNKNOWN: statusEntry('idle', 'Unknown'),
} as const satisfies Record<string, StatusConfig>;

// Dashboard RAG status computed by the server for tasks, incidents, risks and expected system use.
// Green is on track, amber needs a person to act, red has missed its target.
export const DASHBOARD_RAG_STATUS_CONFIG = {
    GREEN: statusEntry('open', 'On track'),
    ORANGE: statusEntry('attention', 'Needs attention'),
    RED: statusEntry('breached', 'Off target'),
} as const satisfies Record<DashboardStatus, StatusConfig>;

// Website checker: the raw page download that precedes a compliance scan.
export const SCRAPED_DATA_STATUS_CONFIG = {
    DOWNLOADING: statusEntry('pending', 'Downloading'),
    ANALYZING: statusEntry('pending', 'Analysing'),
    SUCCESS: statusEntry('succeeded', COMPLIANCE_SCAN_STATUS_LABELS.COMPLETED),
    ERROR: statusEntry('failed', 'Error'),
} as const satisfies Record<ScrapedDataStatus, StatusConfig>;

// User attestation timeline steps (send → fill in → review). `current` is the step waiting on a
// person (the recipient's form, the sender's review), so it calls for attention; `pending` has not
// started; a missed due date is `breached`.
export const ATTESTATION_TIMELINE_STEP_STATUS_CONFIG = {
    completed: statusEntry('succeeded', 'Completed'),
    current: statusEntry('attention', 'Awaiting action'),
    overdue: statusEntry('breached', 'Overdue'),
    pending: statusEntry('idle', 'Not started'),
    cancelled: statusEntry('cancelled', 'Cancelled'),
} as const satisfies Record<'completed' | 'current' | 'overdue' | 'pending' | 'cancelled', StatusConfig>;

export function getMspStatusKey(status: string | null | undefined): keyof typeof MSP_STATUS_CONFIG {
    // Explicit comparison also excludes Object prototype keys such as "constructor".
    switch (status) {
        case 'Onboarding':
        case 'Assessment in progress':
        case 'Accepted Risk':
        case 'Ongoing Monitoring':
        case 'Terminated':
            return status;
        default:
            return 'unknown';
    }
}

const STATUS_CONFIGS = {
    msp: MSP_STATUS_CONFIG,
    vehicleVerification: VEHICLE_VERIFICATION_STATUS_CONFIG,
    incident: INCIDENT_STATUS_CONFIG,
    complaint: COMPLAINT_STATUS_CONFIG,
    commTemplate: COMM_TEMPLATE_STATUS_CONFIG,
    // claim: CLAIM_STATUS_CONFIG,
    complianceAudit: COMPLIANCE_AUDIT_STATUS_CONFIG,
    complianceAuditStep: COMPLIANCE_AUDIT_STEP_STATUS_CONFIG,
    obligation: OBLIGATION_STATUS_CONFIG,
    productVersion: PRODUCT_VERSION_STATUS_CONFIG,
    lodgementLink: LODGEMENT_LINK_STATUS_CONFIG,
    lodgementSubmission: LODGEMENT_SUBMISSION_STATUS_CONFIG,
    aiEvalCase: AI_EVAL_CASE_STATUS_CONFIG,
    aiLodgementRun: AI_LODGEMENT_RUN_STATUS_CONFIG,
    claimProductBinding: CLAIM_PRODUCT_BINDING_STATUS_CONFIG,
    claimCompletionRequest: CLAIM_COMPLETION_REQUEST_STATUS_CONFIG,
    claimClosureOutcome: CLAIM_CLOSURE_OUTCOME_STATUS_CONFIG,
    productVersionLossDateFit: PRODUCT_VERSION_LOSS_DATE_FIT_STATUS_CONFIG,
    integrationStep: INTEGRATION_STEP_STATUS_CONFIG,
    complianceAuditTemplateVersion: COMPLIANCE_AUDIT_TEMPLATE_VERSION_STATUS_CONFIG,
    fsg: FSG_FINDING_STATUS_CONFIG,
    task: TASK_STATUS_CONFIG,
    documentAnalysis: DOCUMENT_ANALYSIS_STATUS_CONFIG,
    emailStatus: EMAIL_STATUS_CONFIG,
    messageClassification: MESSAGE_CLASSIFICATION_STATUS_CONFIG,
    userAttestation: USER_ATTESTATION_STATUS_CONFIG,
    userAttestationSchedule: USER_ATTESTATION_SCHEDULE_STATUS_CONFIG,
    attestationRound: ATTESTATION_ROUND_STATUS_CONFIG,
    formSchema: FORM_SCHEMA_STATUS_CONFIG,
    pdsProcessing: PDS_PROCESSING_STATUS_CONFIG,
    extraction: EXTRACTION_STATUS_CONFIG,
    vehicleInspection: VEHICLE_INSPECTION_STATUS_CONFIG,
    claimAuthorisation: CLAIM_AUTHORISATION_STATUS_CONFIG,
    serviceApiToken: SERVICE_API_TOKEN_STATUS_CONFIG,
    domainAuth: DOMAIN_AUTH_STATUS_CONFIG,
    training: TRAINING_STATUS_CONFIG,
    publication: PUBLICATION_STATUS_CONFIG,
    run: RUN_STATUS_CONFIG,
    orgDigestReport: ORG_DIGEST_REPORT_STATUS_CONFIG,
    excess: EXCESS_STATUS_CONFIG,
    complianceScan: WEBSITE_SCAN_STATUS_CONFIG,
    complianceFinding: COMPLIANCE_FINDING_STATUS_CONFIG,
    llmRequest: LLM_REQUEST_STATUS_CONFIG,
    heapSnapshotExport: HEAP_SNAPSHOT_EXPORT_STATUS_CONFIG,
    inboundEmailArchive: INBOUND_EMAIL_ARCHIVE_STATUS_CONFIG,
    contactVulnerabilityFlag: CONTACT_VULNERABILITY_FLAG_STATUS_CONFIG,
    messageVulnerability: MESSAGE_VULNERABILITY_STATUS_CONFIG,
    vehicleAssessment: VEHICLE_ASSESSMENT_STATUS_CONFIG,
    agentAssessment: AGENT_ASSESSMENT_STATUS_CONFIG,
    supportSession: SUPPORT_SESSION_STATUS_CONFIG,
    importRow: IMPORT_ROW_STATUS_CONFIG,
    weaviateCleanup: WEAVIATE_CLEANUP_STATUS_CONFIG,
    weaviateIdentityCheck: WEAVIATE_IDENTITY_CHECK_STATUS_CONFIG,
    circuitBreaker: CIRCUIT_BREAKER_STATUS_CONFIG,
    syncCampaign: SYNC_CAMPAIGN_STATUS_CONFIG,
    postgresSourceRow: POSTGRES_SOURCE_ROW_STATUS_CONFIG,
    mxRecord: MX_RECORD_STATUS_CONFIG,
    inboundParseHealth: INBOUND_PARSE_HEALTH_STATUS_CONFIG,
    dbUpgrade: DB_UPGRADE_STATUS_CONFIG,
    evalResultChange: EVAL_RESULT_CHANGE_STATUS_CONFIG,
    masterTrackingImplementation: MASTER_TRACKING_IMPLEMENTATION_STATUS_CONFIG,
    subsystemCheck: SUBSYSTEM_CHECK_STATUS_CONFIG,
    liveConnection: LIVE_CONNECTION_STATUS_CONFIG,
    auth0Account: AUTH0_ACCOUNT_STATUS_CONFIG,
    registrationJourney: REGISTRATION_JOURNEY_STATUS_CONFIG,
    termsAcceptance: TERMS_ACCEPTANCE_STATUS_CONFIG,
    userAccount: USER_ACCOUNT_STATUS_CONFIG,
    userInvitation: USER_INVITATION_STATUS_CONFIG,
    authEvent: AUTH_EVENT_STATUS_CONFIG,
    auth0ProfileSync: AUTH0_PROFILE_SYNC_STATUS_CONFIG,
    aiFindingReview: AI_FINDING_REVIEW_STATUS_CONFIG,
    portalUser: PORTAL_USER_STATUS_CONFIG,
    fleetSla: FLEET_SLA_STATUS_CONFIG,
    controlTest: CONTROL_TEST_STATUS_CONFIG,
    stripeInvoice: STRIPE_INVOICE_STATUS_CONFIG,
    complianceOverallResult: COMPLIANCE_OVERALL_RESULT_CONFIG,
    vehicleInspectionTriage: VEHICLE_INSPECTION_TRIAGE_CONFIG,
    messageClassificationVerdict: MESSAGE_CLASSIFICATION_VERDICT_CONFIG,
    linkedClaimDecision: LINKED_CLAIM_DECISION_CONFIG,
    claimDecision: CLAIM_DECISION_CONFIG,
    claimStatusType: CLAIM_STATUS_TYPE_CONFIG,
    msprGovernanceAssessment: MSPR_GOVERNANCE_ASSESSMENT_STATUS_CONFIG,
    repairStage: REPAIR_STAGE_STATUS_CONFIG,
    manualRepairBooking: MANUAL_REPAIR_BOOKING_STATUS_CONFIG,
    personalAccessToken: PERSONAL_ACCESS_TOKEN_STATUS_CONFIG,
    integrationTestResult: INTEGRATION_TEST_RESULT_STATUS_CONFIG,
    job: JOB_STATUS_CONFIG,
    senderEmailVerification: SENDER_EMAIL_VERIFICATION_STATUS_CONFIG,
    documentCompliance: DOCUMENT_COMPLIANCE_STATUS_CONFIG,
    aiSuggestedFlag: AI_SUGGESTED_FLAG_STATUS_CONFIG,
    auditFileScanOutcome: AUDIT_FILE_SCAN_OUTCOME_STATUS_CONFIG,
    auditExecution: AUDIT_EXECUTION_STATUS_CONFIG,
    fileRecord: FILE_RECORD_STATUS_CONFIG,
    state: GENERIC_STATE_CONFIG,
    dashboardRag: DASHBOARD_RAG_STATUS_CONFIG,
    scrapedData: SCRAPED_DATA_STATUS_CONFIG,
    attestationTimelineStep: ATTESTATION_TIMELINE_STEP_STATUS_CONFIG,
} as const;

export type StatusModule = keyof typeof STATUS_CONFIGS;

/**
 * Get the full status configuration for a module and status value
 */
export function getStatusConfig(module: StatusModule, status: string): StatusConfig | undefined {
    const map = STATUS_CONFIGS[module] as Record<string, StatusConfig> | undefined;
    return map && Object.hasOwn(map, status) ? map[status] : undefined;
}

/**
 * Get just the Ant Design colour for a status
 */
export function getStatusColor(module: StatusModule, status: string): string {
    return getStatusConfig(module, status)?.color || 'default';
}

/**
 * Get the human-readable label for a status
 */
export function getStatusLabel(module: StatusModule, status: string): string {
    return getStatusConfig(module, status)?.label || status;
}
